import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import Helpers from 'utils/helpers';
import Plaintext from './plaintext';
import Inputs from 'components/inputs/inputs';
import useInput from 'hooks/input';
import { DatePickerModal, TimePickerModal, DateTimePickerModal } from 'components/common/datepicker/datepickerModal';
import moment from 'moment';
import './date.css';

export function Date(props) {
  return <DateInputWrapper type='date' {...props} />;
}

export function DateTime(props) {
  return <DateInputWrapper type='datetime' {...props} />;
}

export function Time(props) {
  return <DateInputWrapper type='time' {...props} />;
}

function DateInputWrapper(props) {
  const type = Helpers.String.toLower(props.type);
  if (props.plaintext || props.readOnly || props.readonly || props.disabled) {
    let selectedValue = moment(Helpers.Object.exists(props.value) ? props.value : (Helpers.Object.exists(props.defaultValue) ? props.defaultValue : ''));
    let selectedText = '';
    if (selectedValue.isValid()) {
      if (type === 'datetime') {
        selectedText = getDateTimeText(selectedValue);
      }
      else if (type === 'time') {
        selectedText = getTimeText(selectedValue);
      }
      else {
        selectedText = getDateText(selectedValue);
      }
    }
    return <Plaintext style={props.style}>{selectedText}</Plaintext>;
  }
  else {
    if (type === 'datetime') {
      return <DateTimeInput {...props} />;
    }
    else if (type === 'time') {
      return <TimeInput {...props} />;
    }
    else {
      return <DateInput {...props} />;
    }
  }
}

function DateInput(props) {
  const { setInputRef, isInputValid, getInputValidityMessage } = useInput({ description: props.description, hasCustomError: (props.isInvalid && props.feedback), customError: props.feedback });

  const [showDatePickerPopup, setShowDatePickerPopup] = useState(false);
  const [dateSelected, setDateSelected] = useState(new Date());
  const [isDateSelected, setIsDateSelected] = useState(false);

  useEffect(() => {
    if (props.value && moment(props.value).isValid()) {
      setDateSelected(moment(props.value).toDate());
      setIsDateSelected(true);
    }
    else {
      setIsDateSelected(false);
    }
  }, [props.value]);

  const handleClose = () => {
    setShowDatePickerPopup(false);
  };

  const showModalPopupClicked = () => {
    setShowDatePickerPopup(true);
  };

  const handleChangeDateSelection = (date) => {
    setIsDateSelected(true);
    setDateSelected(date);
    if (props.onChange) {
      props.onChange(date);
    }
    if (props.onValueChange) {
      props.onValueChange(date);
    }
  };

  const handleClearDate = () => {
    setIsDateSelected(false);
    setDateSelected(props.value ? props.value : new Date());
    if (props.onChange) {
      props.onChange(null);
    }
    if (props.onValueChange) {
      props.onValueChange(null);
    }
  };

  const renderButton = () => {
    if (props.disabled) {
      return null;
    }
    if (isDateSelected) {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-delete' onClick={handleClearDate} />
        </div>
      );
    }
    else {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-calendar' onClick={showModalPopupClicked} />
        </div>
      );
    }
  };

  const renderFeedback = () => {
    if (!isInputValid()) {
      return <div className='invalid-feedback'>{getInputValidityMessage()}</div>;
    }
    else if (props.isInvalid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='invalid-feedback'>{props.feedback}</div>;
    }
    else if (props.isValid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='valid-feedback'>{props.feedback}</div>;
    }
    return null;
  };

  const renderHelp = () => {
    if (props.help && !Helpers.String.isEmpty(props.help)) {
      return <div className='help-feedback'>{props.help}</div>;
    }
    return null;
  };

  return (
    <>
      <div className='datepicker-form-control input-group'>
        <Form.Control
          type='text'
          className={props.className}
          style={props.style}
          placeholder={props.placeholder}
          value={getDateText(dateSelected, !isDateSelected)}
          onClick={showModalPopupClicked}
          onChange={() => null}
          onFocus={(e) => e.currentTarget.blur()}
          required={props.required}
          disabled={props.disabled}
          ref={setInputRef}
          isInvalid={props.isInvalid && props.alwaysShowValidity}
          isValid={props.isValid && props.alwaysShowValidity}
        />
        {renderButton()}
        {renderHelp()}
        {renderFeedback()}
      </div>
      <DatePickerModal show={showDatePickerPopup} date={dateSelected} altDate={props.altDate} onClose={handleClose} onChange={handleChangeDateSelection} />
    </>
  );
}

function DateTimeInput(props) {
  const { setInputRef, isInputValid, getInputValidityMessage } = useInput({ description: props.description, hasCustomError: (props.isInvalid && props.feedback), customError: props.feedback });

  const [showDateTimePickerPopup, setShowDateTimePickerPopup] = useState(false);
  const [dateTimeSelected, setDateTimeSelected] = useState(new Date());
  const [isDateTimeSelected, setIsDateTimeSelected] = useState(false);

  useEffect(() => {
    if (props.value && moment(props.value).isValid()) {
      setDateTimeSelected(moment(props.value).toDate());
      setIsDateTimeSelected(true);
    }
    else {
      setIsDateTimeSelected(false);
    }
  }, [props.value]);

  const handleClose = () => {
    setShowDateTimePickerPopup(false);
  };

  const showModalPopupClicked = () => {
    setShowDateTimePickerPopup(true);
  };

  const handleChangeDateTimeSelection = (datetime) => {
    setIsDateTimeSelected(true);
    setDateTimeSelected(datetime);
    if (props.onChange) {
      props.onChange(datetime);
    }
    if (props.onValueChange) {
      props.onValueChange(datetime);
    }
  };

  const handleClearDateTime = () => {
    setIsDateTimeSelected(false);
    setDateTimeSelected(props.value ? props.value : new Date());
    if (props.onChange) {
      props.onChange(null);
    }
    if (props.onValueChange) {
      props.onValueChange(null);
    }
  };

  const renderButton = () => {
    if (props.disabled) {
      return null;
    }
    if (isDateTimeSelected) {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-delete' onClick={handleClearDateTime} />
        </div>
      );
    }
    else {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-calendar' onClick={showModalPopupClicked} />
        </div>
      );
    }
  };

  const renderFeedback = () => {
    if (!isInputValid()) {
      return <div className='invalid-feedback'>{getInputValidityMessage()}</div>;
    }
    else if (props.isInvalid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='invalid-feedback ml-1'>{props.feedback}</div>;
    }
    else if (props.isValid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='valid-feedback ml-1'>{props.feedback}</div>;
    }
    return null;
  };

  const renderHelp = () => {
    if (props.help && !Helpers.String.isEmpty(props.help)) {
      return <div className='help-feedback'>{props.help}</div>;
    }
    return null;
  };

  return (
    <>
      <div className='datepicker-form-control input-group'>
        <Form.Control
          type='text'
          className={props.className}
          placeholder={props.placeholder}
          value={getDateTimeText(dateTimeSelected, !isDateTimeSelected)}
          onClick={showModalPopupClicked}
          onChange={() => null}
          onFocus={(e) => e.currentTarget.blur()}
          required={props.required}
          disabled={props.disabled}
          ref={setInputRef}
          isInvalid={props.isInvalid && props.alwaysShowValidity}
          isValid={props.isValid && props.alwaysShowValidity}
        />
        {renderButton()}
        {renderHelp()}
        {renderFeedback()}
      </div>
      <DateTimePickerModal date={dateTimeSelected} altDate={props.altDate} show={showDateTimePickerPopup} onClose={handleClose} onChange={handleChangeDateTimeSelection} />
    </>
  );
}

function TimeInput(props) {
  const { setInputRef, isInputValid, getInputValidityMessage } = useInput({ description: props.description, hasCustomError: (props.isInvalid && props.feedback), customError: props.feedback });

  const [showTimePickerPopup, setShowTimePickerPopup] = useState(false);
  const [hourSelected, setHourSelected] = useState(0);
  const [minutesSelected, setMinutesSelected] = useState(0);
  const [isTimeSelected, setIsTimeSelected] = useState(false);

  useEffect(() => {
    if (props.value && moment(props.value).isValid()) {
      setHourSelected(moment(props.value).hours());
      setMinutesSelected(moment(props.value).minutes());
      setIsTimeSelected(true);
    }
    else {
      setIsTimeSelected(false);
    }
  }, [props.value]);

  const createDate = (h, m) => {
    let d = (moment(props.value).isValid()) ? moment(props.value).toDate() : new Date();
    d.setHours(h);
    d.setMinutes(m);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
  };

  const handleClose = () => {
    setShowTimePickerPopup(false);
  };

  const showModalPopupClicked = () => {
    setShowTimePickerPopup(true);
  };

  const handleChangeTimeSelection = (hour, minute) => {
    setIsTimeSelected(true);
    setHourSelected(hour);
    setMinutesSelected(minute);
    if (props.onChange) {
      props.onChange(createDate(hour, minute));
    }
    if (props.onValueChange) {
      props.onValueChange(createDate(hour, minute));
    }
  };

  const handleClearTime = () => {
    setIsTimeSelected(false);
    setHourSelected(0);
    setMinutesSelected(0);
    if (props.onChange) {
      props.onChange(null);
    }
    if (props.onValueChange) {
      props.onValueChange(null);
    }
  };

  const renderButton = () => {
    if (props.disabled) {
      return null;
    }
    if (isTimeSelected) {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-delete' onClick={handleClearTime} />
        </div>
      );
    }
    else {
      return (
        <div className='input-group-append'>
          <Inputs.Button variant='form' iconClass='icons8-clock' onClick={showModalPopupClicked} />
        </div>
      );
    }
  };

  const renderFeedback = () => {
    if (!isInputValid()) {
      return <div className='invalid-feedback'>{getInputValidityMessage()}</div>;
    }
    else if (props.isInvalid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='invalid-feedback'>{props.feedback}</div>;
    }
    else if (props.isValid && props.feedback && !Helpers.String.isEmpty(props.feedback)) {
      return <div className='valid-feedback'>{props.feedback}</div>;
    }
    return null;
  };

  const renderHelp = () => {
    if (props.help && !Helpers.String.isEmpty(props.help)) {
      return <div className='help-feedback'>{props.help}</div>;
    }
    return null;
  };

  return (
    <>
      <div className='datepicker-form-control input-group'>
        <Form.Control
          type='text'
          className={props.className}
          placeholder={props.placeholder}
          value={getTimeText(createDate(hourSelected, minutesSelected), !isTimeSelected)}
          onClick={showModalPopupClicked}
          onChange={() => null}
          onFocus={(e) => e.currentTarget.blur()}
          required={props.required}
          disabled={props.disabled}
          ref={setInputRef}
          isInvalid={props.isInvalid && props.alwaysShowValidity}
          isValid={props.isValid && props.alwaysShowValidity}
        />
        {renderButton()}
        {renderHelp()}
        {renderFeedback()}
      </div>
      <TimePickerModal hour={hourSelected} minute={minutesSelected} show={showTimePickerPopup} onClose={handleClose} onChange={handleChangeTimeSelection} />
    </>
  );
}

const getDateText = (value, overrideEmpty) => {
  if (overrideEmpty) {
    return '';
  }
  return moment(value).format('L')
};

const getDateTimeText = (value, overrideEmpty) => {
  if (overrideEmpty) {
    return '';
  }
  return moment(value).format('L LT')
};

const getTimeText = (value, overrideEmpty) => {
  if (overrideEmpty) {
    return '';
  }
  return moment(value).format('LT');
};